import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { inboxFirestore } from '../../services/firebase';
import {
	collection,
	doc,
	getDocs,
	query,
	setDoc,
	where,
	deleteDoc,
	updateDoc,
	getDoc,
	orderBy,
} from 'firebase/firestore';
import { useSelector } from 'react-redux';
import { RootState } from '..';

export interface Group {
	id?: string;
	name: string;
	emailNotification: string;
	phoneNumbers?: string[];
	isFranchise: boolean;
	deleted?: boolean;
}

export interface GroupState {
	groups: Group[];
	loading: boolean;
	error: string | null;
}

const initialState: GroupState = {
	groups: [],
	loading: false,
	error: null,
};

// Create a new group
export const createGroup = createAsyncThunk(
	'groups/createGroup',
	async (GroupForm: Group, thunkApi) => {
		try {
			const ref = doc(collection(inboxFirestore, 'groups'));
			const newGroup = {
				id: ref.id,
				deleted: false,
				...GroupForm,
			};

			await setDoc(ref, newGroup);

			return newGroup;
		} catch (error) {
			return thunkApi.rejectWithValue(error);
		}
	}
);

// Update a group
export const updateGroup = createAsyncThunk(
	'groups/updateGroup',
	async (group: Group, thunkApi) => {
		try {
			if (!group.id) {
				throw new Error('ID do grupo está vazio!');
			}

			const ref = doc(inboxFirestore, 'groups', group.id ?? '');

			await updateDoc(ref, group as any);
			return group;
		} catch (error) {
			return thunkApi.rejectWithValue(error);
		}
	}
);

// Delete a group
export const deleteGroup = createAsyncThunk(
	'groups/deleteGroup',
	async (groupId: string, thunkApi) => {
		try {
			const ref = doc(inboxFirestore, 'groups', groupId);
			await updateDoc(ref, {
				deleted: true,
				deletedAt: Date.now(),
			});

			return groupId;
		} catch (error) {
			return thunkApi.rejectWithValue(error);
		}
	}
);

// Get all groups
export const getGroups = createAsyncThunk('groups/getGroups', async () => {
	const ref = query(collection(inboxFirestore, 'groups'), orderBy('name'));

	const snapshot = await getDocs(ref);

	const groups: Group[] = [];
	snapshot.forEach((doc) => {
		groups.push(doc.data() as Group);
	});
	return groups.filter((g) => !g.deleted);
});

export const groupSlice = createSlice({
	name: 'groups',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(createGroup.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(createGroup.fulfilled, (state, action) => {
				state.loading = false;
				state.error = null;
				const exists = state.groups.find((g) => g.id === action.payload.id);
				if (!exists) {
					state.groups.push(action.payload);
				}
			})
			.addCase(createGroup.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			.addCase(updateGroup.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(updateGroup.fulfilled, (state, action) => {
				state.loading = false;
				state.error = null;
				state.groups = state.groups.map((group) =>
					group.id === action.payload.id ? action.payload : group
				);
			})
			.addCase(updateGroup.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			.addCase(deleteGroup.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(deleteGroup.fulfilled, (state, action) => {
				state.loading = false;
				state.error = null;
				state.groups = state.groups.filter((group) => group.id !== action.payload);
			})
			.addCase(deleteGroup.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			.addCase(getGroups.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(getGroups.fulfilled, (state, action) => {
				state.loading = false;
				state.error = null;
				state.groups = action.payload;
			});
	},
});

export const useGroups = () => useSelector<RootState, GroupState>((state) => state.groups);
export default groupSlice.reducer;
